import { useRef, useEffect, useState } from 'react';
import { useSimulation } from '@/hooks/useSimulation';
import { ViewMode } from '@/types/simulation';
import { useTheme, ThemeMode } from '@/contexts/ThemeContext';
import FunctionInputSection from './components/FunctionInputSection';
import GraphSettingsSection from './components/GraphSettingsSection';
import ObjectSettingsSection from './components/ObjectSettingsSection';
import SimulationControls from './components/SimulationControls';
import ResultsPanel from './components/ResultsPanel';
import GraphCanvas from './components/GraphCanvas';
import GraphCanvas3D from './components/GraphCanvas3D';
import SplashScreen, { KGIcon } from './components/SplashScreen';
import ImportModal from './components/ImportModal';
import ExportModal from './components/ExportModal';
import PresetPanel from './components/PresetPanel';
import { FunctionEntry } from '@/types/simulation';

type SideTab = 'function' | 'object' | 'settings' | 'preset';

const THEME_OPTIONS: { mode: ThemeMode; icon: string; label: string }[] = [
  { mode: 'dark',    icon: 'ri-moon-fill',        label: 'ダーク' },
  { mode: 'light',   icon: 'ri-sun-line',          label: 'ライト' },
  { mode: 'picture', icon: 'ri-image-2-line',      label: '印刷' },
];

export default function HomePage() {
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [sideTab, setSideTab] = useState<SideTab>('function');
  const [viewMode, setViewMode] = useState<ViewMode>('2d');
  const [modeFlipping, setModeFlipping] = useState(false);
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const [themeSwitching, setThemeSwitching] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [imageOverlay, setImageOverlay] = useState<{ dataUrl: string; width: number; height: number } | null>(null);
  // Always show splash on every page load / reset
  const [splashDone, setSplashDone] = useState(false);
  const [splashKey, setSplashKey] = useState(0);
  const { theme, setTheme } = useTheme();

  const handleSplashFinished = () => {
    setSplashDone(true);
  };

  useEffect(() => {
    const measure = () => {
      if (canvasContainerRef.current) {
        setCanvasSize({
          width: canvasContainerRef.current.clientWidth,
          height: canvasContainerRef.current.clientHeight,
        });
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (canvasContainerRef.current) ro.observe(canvasContainerRef.current);
    return () => ro.disconnect();
  }, []);

  const sim = useSimulation(canvasSize.width, canvasSize.height);

  const handleObjectDrag = (id: string, wx: number, wy: number) => {
    sim.updateObject(id, { x: wx, y: wy });
  };

  const handleLogoClick = () => {
    setResetConfirmOpen(true);
  };

  const handleConfirmReset = () => {
    sim.resetAll();
    setResetConfirmOpen(false);
    setSplashDone(false);
    setSplashKey((k) => k + 1);
  };

  const handleImport = (
    importedFunctions: Omit<FunctionEntry, 'id'>[],
    overlay?: { dataUrl: string; width: number; height: number }
  ) => {
    if (overlay) {
      setImageOverlay(overlay);
    }
    importedFunctions.forEach((fn) => {
      sim.addFunctionWithData(fn);
    });
    setImportModalOpen(false);
  };

  const toggleViewMode = () => {
    setModeFlipping(true);
    setTimeout(() => {
      setViewMode((m) => (m === '2d' ? '3d' : '2d'));
      setModeFlipping(false);
    }, 250);
  };

  const handleThemeChange = (t: ThemeMode) => {
    if (t === theme) { setThemeMenuOpen(false); return; }
    setThemeSwitching(true);
    setTimeout(() => {
      setTheme(t);
      setThemeSwitching(false);
    }, 200);
    setThemeMenuOpen(false);
  };

  const currentThemeOption = THEME_OPTIONS.find((o) => o.mode === theme) ?? THEME_OPTIONS[0];

  // ---- Dynamic theme-aware colors (CSS-variable-based for theme switching) ----
  const isDark = theme === 'dark';

  // Use CSS variables via Tailwind arbitrary values for proper theme switching
  const textPrimary   = 'text-[var(--text-primary)]';
  const textSecondary = 'text-[var(--text-secondary)]';
  const textMuted     = 'text-[var(--text-muted)]';
  const borderSubtle  = 'border-[var(--border-subtle)]';
  const borderMid     = 'border-[var(--border-mid)]';

  const accentTime   = 'text-[var(--accent-cyan)]';
  const accentStatus = sim.simState.running
    ? 'text-emerald-500'
    : 'text-[var(--text-muted)]';

  return (
    <>
      {!splashDone && <SplashScreen key={splashKey} onFinished={handleSplashFinished} />}
    <div
      className={`flex flex-col h-screen overflow-hidden transition-colors duration-300 ${themeSwitching ? 'opacity-0' : 'opacity-100'}`}
      style={{ background: 'var(--bg-base)' }}
    >
      {/* ===== HEADER ===== */}
      <header
        className={`flex-shrink-0 flex items-center justify-between px-5 py-2.5 border-b ${borderSubtle} z-50 glass-panel`}
        style={{ borderLeft: 'none', borderRight: 'none', borderTop: 'none', borderRadius: 0 }}
      >
        {/* Logo — click to reset all */}
        <button
          onClick={handleLogoClick}
          className={`flex items-center gap-3 ripple-btn rounded-xl px-2 py-1 -mx-2 -my-1 transition-all duration-200 hover:scale-105 cursor-pointer group`}
          title="クリックで全体リセット"
        >
          <div
            className="w-8 h-8 flex items-center justify-center rounded-xl transition-all duration-200 group-hover:scale-110 overflow-hidden relative"
            style={{ background: 'linear-gradient(135deg, rgba(0,180,220,0.15), rgba(0,140,180,0.08))', border: '1px solid rgba(0,180,220,0.20)' }}
          >
            {/* Subtle grid pattern background */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              {/* Horizontal grid lines */}
              <line x1="0" y1="8"  x2="32" y2="8"  stroke="rgba(0,217,255,0.07)" strokeWidth="0.5" />
              <line x1="0" y1="16" x2="32" y2="16" stroke="rgba(0,217,255,0.10)" strokeWidth="0.5" />
              <line x1="0" y1="24" x2="32" y2="24" stroke="rgba(0,217,255,0.07)" strokeWidth="0.5" />
              {/* Vertical grid lines */}
              <line x1="8"  y1="0" x2="8"  y2="32" stroke="rgba(0,217,255,0.07)" strokeWidth="0.5" />
              <line x1="16" y1="0" x2="16" y2="32" stroke="rgba(0,217,255,0.10)" strokeWidth="0.5" />
              <line x1="24" y1="0" x2="24" y2="32" stroke="rgba(0,217,255,0.07)" strokeWidth="0.5" />
            </svg>
            <KGIcon size={18} />
          </div>
          <div className="text-left">
            <h1 className="font-bold text-sm leading-tight tracking-tight" style={{ color: 'var(--text-primary)' }}>kuregraph</h1>
            <p className="text-xs leading-none" style={{ color: 'var(--text-muted)' }}>数学 ＋ 物理シミュレーター</p>
          </div>
        </button>

        {/* Center: 2D / 3D toggle */}
        <div className="absolute left-1/2 -translate-x-1/2">
          <div className="glass rounded-full p-1 flex items-center gap-1">
            {(['2d', '3d'] as ViewMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => viewMode !== mode && toggleViewMode()}
                style={{ color: viewMode === mode ? 'var(--text-primary)' : 'var(--text-muted)' }}
                className={`ripple-btn relative px-5 py-1.5 rounded-full text-xs font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap uppercase tracking-widest ${
                  viewMode === mode ? 'shadow-lg' : ''
                }`}
              >
                {viewMode === mode && (
                  <span className="absolute inset-0 rounded-full glass-btn-active" />
                )}
                <span className="relative z-10">{mode}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Right: status + theme switcher */}
        <div className="flex items-center gap-2">
          {/* Running status */}
          <div className={`glass-pill flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-all duration-500 ${sim.simState.running ? 'border-emerald-400/30' : ''}`}>
            <span className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${sim.simState.running ? 'bg-emerald-400 animate-pulse' : (isDark ? 'bg-white/20' : 'bg-gray-300')}`} />
            <span className={`font-mono font-medium ${accentStatus}`}>
              {sim.simState.running ? '実行中' : '停止中'}
            </span>
          </div>
          {/* Time */}
          <div className="glass-pill px-3 py-1.5 rounded-full">
            <span className={`text-xs font-mono ${textMuted}`}>t = </span>
            <span className={`text-xs font-mono ${accentTime}`}>{sim.simState.time.toFixed(2)}s</span>
          </div>

          {/* Export button */}
          <button
            onClick={() => setExportModalOpen(true)}
            className={`no-print glass-btn ripple-btn px-3 py-1.5 rounded-full flex items-center gap-1.5 cursor-pointer whitespace-nowrap transition-all ${textSecondary} hover:${textPrimary}`}
            title="エクスポート"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-export-line text-sm" />
            </div>
            <span className="text-xs font-medium">書き出し</span>
          </button>

          {/* Theme switcher button */}
          <div className="relative no-print">
            <button
              onClick={() => setThemeMenuOpen(!themeMenuOpen)}
              className={`glass-btn ripple-btn px-3 py-1.5 rounded-full flex items-center gap-1.5 cursor-pointer whitespace-nowrap transition-all ${textSecondary} hover:${textPrimary}`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${currentThemeOption.icon} text-sm`} />
              </div>
              <span className="text-xs font-medium">{currentThemeOption.label}</span>
              <div className="w-3 h-3 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${themeMenuOpen ? 'rotate-180' : ''}`} />
              </div>
            </button>

            {themeMenuOpen && (
              <div className="absolute right-0 top-full mt-2 glass rounded-xl overflow-hidden shadow-xl z-50 animate-float-in min-w-[140px]">
                {THEME_OPTIONS.map(({ mode, icon, label }) => (
                  <button
                    key={mode}
                    onClick={() => handleThemeChange(mode)}
                    className={`ripple-btn w-full flex items-center gap-3 px-4 py-2.5 text-sm cursor-pointer whitespace-nowrap transition-all hover:glass-btn-active ${
                      theme === mode
                        ? `${textPrimary} glass-btn-active`
                        : `${textSecondary}`
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${icon} text-sm`} />
                    </div>
                    {label}
                    {theme === mode && (
                      <div className="ml-auto w-3 h-3 flex items-center justify-center">
                        <i className="ri-check-line text-xs text-emerald-500" />
                      </div>
                    )}
                  </button>
                ))}
                {/* Picture mode hint */}
                <div className={`px-4 py-2 border-t ${borderSubtle}`}>
                  <p className={`text-xs ${textMuted} leading-snug`}>
                    印刷モード：グラフのみ印刷可
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Close theme menu on outside click — z-40 but below header z-30 content */}
      {themeMenuOpen && (
        <div className="fixed inset-0 z-40" style={{ pointerEvents: 'all' }} onClick={() => setThemeMenuOpen(false)} />
      )}

      {/* ===== EXPORT MODAL ===== */}
      {exportModalOpen && (
        <ExportModal
          functions={sim.functions}
          objects={sim.objects}
          settings={sim.graphSettings}
          canvasWidth={canvasSize.width}
          canvasHeight={canvasSize.height}
          onClose={() => setExportModalOpen(false)}
        />
      )}

      {/* ===== IMPORT MODAL ===== */}
      {importModalOpen && (
        <ImportModal
          functions={sim.functions}
          onImport={handleImport}
          onClose={() => setImportModalOpen(false)}
        />
      )}

      {/* ===== RESET CONFIRM MODAL ===== */}
      {resetConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setResetConfirmOpen(false)}
          />
          {/* Modal */}
          <div
            className="relative glass rounded-2xl p-6 w-80 animate-float-in"
            style={{ boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}
          >
            {/* Icon */}
            <div className="flex items-center justify-center mb-4">
              <div
                className="w-12 h-12 flex items-center justify-center rounded-2xl"
                style={{ background: 'linear-gradient(135deg, rgba(0,180,220,0.18), rgba(0,130,180,0.10))', border: '1px solid rgba(0,180,220,0.25)' }}
              >
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-refresh-line text-xl" style={{ color: 'var(--accent-cyan)' }} />
                </div>
              </div>
            </div>
            <h2 className={`${textPrimary} text-base font-bold text-center mb-1`}>全体をリセット</h2>
            <p className={`${textMuted} text-xs text-center mb-5 leading-relaxed`}>
              関数・物体・グラフ設定・シミュレーション<br />すべてを初期状態に戻します。
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setResetConfirmOpen(false)}
                className={`glass-btn ripple-btn flex-1 py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap ${textMuted} hover:${textPrimary} transition-all`}
              >
                キャンセル
              </button>
              <button
                onClick={handleConfirmReset}
                className="ripple-btn flex-1 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap text-white transition-all bg-red-500/70 border border-red-400/50 hover:bg-red-500 hover:scale-105"
              >
                リセット
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MAIN LAYOUT ===== */}
      <div className="flex flex-1 overflow-hidden">

        {/* LEFT PANEL */}
        <aside className={`no-print w-72 flex-shrink-0 flex flex-col overflow-hidden border-r ${borderMid} glass-panel`}>
          {/* Tab row */}
          <div className={`flex p-2 gap-1 border-b ${borderMid} flex-shrink-0`}>
            {(
              [
                { key: 'function' as SideTab, label: '関数', icon: 'ri-function-line', color: isDark ? 'text-cyan-300' : 'text-sky-600' },
                { key: 'object' as SideTab, label: '物体', icon: 'ri-planet-line', color: isDark ? 'text-orange-300' : 'text-orange-600' },
                { key: 'settings' as SideTab, label: '範囲', icon: 'ri-equalizer-line', color: isDark ? 'text-purple-300' : 'text-violet-600' },
                { key: 'preset' as SideTab, label: '保存', icon: 'ri-bookmark-line', color: isDark ? 'text-amber-300' : 'text-amber-600' },
              ]
            ).map(({ key, label, icon, color }) => (
              <button
                key={key}
                onClick={() => setSideTab(key)}
                className={`ripple-btn flex-1 py-1.5 px-1.5 rounded-lg text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-1 ${
                  sideTab === key ? `glass-btn-active ${color}` : `${textMuted} hover:${textSecondary}`
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${icon} text-xs`} />
                </div>
                {label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
            {sideTab === 'function' && (
              <FunctionInputSection
                functions={sim.functions}
                onAdd={sim.addFunction}
                onUpdate={sim.updateFunction}
                onRemove={sim.removeFunction}
                onResetAll={() => sim.resetAll()}
                onOpenImport={() => setImportModalOpen(true)}
              />
            )}
            {sideTab === 'object' && (
              <ObjectSettingsSection
                objects={sim.objects}
                selectedObjectId={sim.selectedObjectId}
                onSelect={sim.setSelectedObjectId}
                onUpdate={sim.updateObject}
                onAdd={sim.addObject}
                onRemove={sim.removeObject}
                objectCollisions={sim.simState.objectCollisions}
                onToggleCollisions={() => sim.setSimState((prev) => ({ ...prev, objectCollisions: !prev.objectCollisions }))}
              />
            )}
            {sideTab === 'settings' && (
              <GraphSettingsSection
                settings={sim.graphSettings}
                onChange={(u) => sim.setGraphSettings((prev) => ({ ...prev, ...u }))}
              />
            )}
            {sideTab === 'preset' && (
              <PresetPanel
                presets={sim.presets}
                hasObjects={sim.objects.length > 0}
                onSave={sim.savePreset}
                onLoad={sim.loadPreset}
                onDelete={sim.deletePreset}
              />
            )}
          </div>
        </aside>

        {/* CANVAS AREA */}
        <main
          className={`flex-1 overflow-hidden relative transition-opacity duration-300 ${modeFlipping ? 'opacity-0' : 'opacity-100'}`}
          ref={canvasContainerRef}
        >
          {viewMode === '2d' ? (
            <GraphCanvas
              functions={sim.functions}
              objects={sim.objects}
              settings={sim.graphSettings}
              showTrails={sim.simState.showTrails}
              selectedObjectId={sim.selectedObjectId}
              onObjectSelect={sim.setSelectedObjectId}
              onObjectDrag={handleObjectDrag}
              onSettingsChange={(u) => sim.setGraphSettings((prev) => ({ ...prev, ...u }))}
              theme={theme}
              imageOverlay={imageOverlay}
              onClearImageOverlay={() => setImageOverlay(null)}
            />
          ) : (
            <GraphCanvas3D
              functions={sim.functions}
              objects={sim.objects}
              settings={sim.graphSettings}
              showTrails={sim.simState.showTrails}
              selectedObjectId={sim.selectedObjectId}
              onObjectSelect={sim.setSelectedObjectId}
              onObjectDrag={handleObjectDrag}
              theme={theme}
            />
          )}
        </main>

        {/* RIGHT PANEL */}
        <aside className={`no-print w-64 flex-shrink-0 flex flex-col overflow-hidden border-l ${borderMid} glass-panel`}>
          <div className="flex-1 overflow-y-auto p-4 space-y-5 custom-scrollbar">
            <SimulationControls
              simState={sim.simState}
              onStart={sim.startSimulation}
              onPause={sim.pauseSimulation}
              onReset={sim.resetSimulation}
              onUpdate={(u) => sim.setSimState((prev) => ({ ...prev, ...u }))}
              isDark={isDark}
            />
            <div className={`border-t ${borderSubtle} pt-4`}>
              <ResultsPanel
                selectedObject={sim.selectedObject}
                simState={sim.simState}
                objects={sim.objects}
                isDark={isDark}
              />
            </div>
          </div>
        </aside>
      </div>
    </div>
    </>
  );
}
